import {
  AuthorizationService,
  AuthService,
  AuthStateAdaptorService,
  CalculatedApplicationPermission,
  ContactUser,
  FeaturePermission,
  UserType,
} from '@aiwyn/auth';
import { ConfigService, LaunchDarklyFlag, LaunchDarklyService } from '@aiwyn/config';
import { EngagementAvaliabilityCheck } from '@aiwyn/engagements';
import { FirmContactService, LegalService } from '@aiwyn/services';
import { SharedState, SnackbarService, SnackbarType } from '@aiwyn/shared';
import { Clipboard } from '@angular/cdk/clipboard';
import { AfterViewInit, Component, DestroyRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { EngagementsApiService } from '../../../api/services/engagements-api.service';
import { CxpTasksSelectors, LoadCxpTaskCount } from '../../../features/cxp';
import { SetEngagementTabCount } from '../../../state';
import {
  ROUTE_TOKEN_CXP_TASKS,
  ROUTE_TOKEN_ENGAGEMENTS_BASE,
  ROUTE_TOKEN_FILES,
  ROUTE_TOKEN_INVOICES,
  ROUTE_TOKEN_MAIN_BASE,
  ROUTE_TOKEN_PAYMENTS,
  ROUTE_TOKEN_PROJECTS,
  ROUTE_TOKEN_SETTINGS,
} from '../../../routing-constants';
import {
  ContactSupportDialogComponent,
  ContactSupportDialogData,
} from '../../../common/layout/components/contact-support-dialog/contact-support-dialog.component';
import { DATA_SELECTOR_ATTRIBUTE } from '../../../shared/directives/data-selector/data-selector.directive';
import { AppState } from '../../../state';

@Component({
  selector: 'app-main-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: false,
})
export class HeaderComponent implements OnInit, AfterViewInit {
  @ViewChild('help', { static: false }) helpMenu: MatMenu;
  @ViewChild(MatMenuTrigger) helpMenuTrigger: MatMenuTrigger;

  public readonly configService = inject(ConfigService);
  public readonly firmContact = inject(FirmContactService);
  public readonly legalService = inject(LegalService);
  private readonly authService = inject(AuthService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly store = inject(Store);
  private readonly authStateAdaptor = inject(AuthStateAdaptorService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly launchDarklyService = inject(LaunchDarklyService);
  private readonly clipboard = inject(Clipboard);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly engagementsApiService = inject(EngagementsApiService);
  private readonly dialog = inject(MatDialog);

  readonly ROUTE_TOKEN_MAIN_BASE = ROUTE_TOKEN_MAIN_BASE;
  readonly ROUTE_TOKEN_ENGAGEMENTS_BASE = ROUTE_TOKEN_ENGAGEMENTS_BASE;
  readonly ROUTE_TOKEN_PROJECTS = ROUTE_TOKEN_PROJECTS;
  readonly ROUTE_TOKEN_CXP_TASKS = ROUTE_TOKEN_CXP_TASKS;
  readonly ROUTE_TOKEN_FILES = ROUTE_TOKEN_FILES;
  readonly ROUTE_TOKEN_INVOICES = ROUTE_TOKEN_INVOICES;
  readonly ROUTE_TOKEN_PAYMENTS = ROUTE_TOKEN_PAYMENTS;
  readonly ROUTE_TOKEN_SETTINGS = ROUTE_TOKEN_SETTINGS;

  bannerMessage = toSignal(this.configService.configsLoaded$.pipe(map(_ => this.configService.bannerMessage)), { initialValue: undefined });
  bannerTheme = toSignal(this.configService.configsLoaded$.pipe(map(_ => this.configService.bannerTheme)), { initialValue: 'WARN' });
  bannerDismissed = signal(false);

  readonly version$: Observable<string> = this.store.select(SharedState.getVersion);

  readonly showEngagementTab$ = combineLatest({
    hasEngagementsPermission: this.authorizationService.hasPermission(FeaturePermission.ENGAGEMENTS),
    showEngagementsTab: this.store.select(AppState.getShowEngagementsTab),
  }).pipe(map(({ hasEngagementsPermission, showEngagementsTab }) => hasEngagementsPermission && showEngagementsTab));

  readonly engagementsTabCount$ = this.store.select(AppState.getEngagementsTabCount);

  private readonly hasClientRequestsAccess$ = this.authorizationService.hasPermission(FeaturePermission.PROJECTS_AND_CLIENT_REQUESTS).pipe(
    switchMap((userHasRoutePermissions) => {
      if (!userHasRoutePermissions) {
        return of(false);
      }
      return this.authService.getUser().pipe(map((user) => user instanceof ContactUser && user.hasClientRequests()));
    }),
  );

  private readonly requestsViewEnabled$ = this.launchDarklyService.getFeatureFlag(LaunchDarklyFlag.CXP_ENABLE_TASKS_VIEW);

  showClientRequestsTab$ = combineLatest({
    hasAccess: this.hasClientRequestsAccess$,
    tasksEnabled: this.requestsViewEnabled$,
  }).pipe(map(({ hasAccess, tasksEnabled }) => hasAccess && !tasksEnabled));

  showRequestsTab$ = combineLatest({
    hasPermission: this.authorizationService.hasPermission(FeaturePermission.PROJECTS_AND_CLIENT_REQUESTS),
    tasksEnabled: this.requestsViewEnabled$,
    userType: this.authStateAdaptor.getUserType(),
  }).pipe(map(({ hasPermission, tasksEnabled, userType }) => hasPermission && tasksEnabled && userType === UserType.CONTACT));

  readonly requestsTabCount$: Observable<number> = this.store.select(CxpTasksSelectors.getBadgeOpenCount);

  protected showFilesTab$ = this.launchDarklyService
    .getFeatureFlag(LaunchDarklyFlag.DMS_FILE_TAB_ENABLED)
    .pipe(switchMap((flag) => (flag ? this.hasClientRequestsAccess$ : of(false))));

  private _invoicesAndPaymentPermission$ = this.authorizationService.hasPermission(FeaturePermission.INVOICES_AND_PAYMENTS);

  showInvoicesTab$: Observable<boolean> = this._invoicesAndPaymentPermission$.pipe(
    map((invoiceAndPaymentPermission) => invoiceAndPaymentPermission && this.configService.enableClientPortalPayments),
  );

  showPaymentsTab$: Observable<boolean> = this._invoicesAndPaymentPermission$.pipe(
    map((invoiceAndPaymentPermission) => invoiceAndPaymentPermission && this.configService.enableClientPortalPayments),
  );

  showSettingsTab$: Observable<boolean> = this.authorizationService.evaluatePermission(CalculatedApplicationPermission.SETTINGS);

  isNotGoogleAccount$: Observable<boolean> = this.authService.getUser(true).pipe(map((user) => !user?.isGoogleAccount()));

  UserType = UserType;
  userType$: Observable<UserType> = this.authStateAdaptor.getUserType();
  userDisplayName$: Observable<string> = this.authService.getUser(true).pipe(map((user) => user?.getDisplayName() ?? ''));

  hasSecureLink$: Observable<boolean> = this.authService.getUser().pipe(map((user) => !!user.getSecureLinkGrants()));

  /**
   * Gates the in-app support form, so a bad rollout is a flag flip rather than a hotfix through the
   * release train. Off hides the row: the `mailto:` it replaced was the dead end this work exists to
   * remove and addressed Aiwyn rather than the firm, so restoring it as the safe state would make
   * the safe state the broken one.
   */
  protected supportFormEnabled$ = this.launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_CLIENT_PORTAL_SUPPORT_FORM);

  displayName = '';
  public isAdmin = false;
  public isTestMode = false;

  /**
   * Opens the in-app support form. Name and email are read here rather than inside the dialog: the
   * auth state can briefly revert just after a dialog opens, and a field that fills then empties
   * reads as a bug. Both are display-only — the server derives the real sender from the session.
   *
   * The destination is not passed in. It is the server's decision and the dialog asks the server for
   * it, so the address shown cannot disagree with the address the send uses.
   */
  openContactSupport(): void {
    const data: ContactSupportDialogData = {
      name: this.authService.getUserSnapshot()?.getDisplayName() ?? '',
      email: this.authStateAdaptor.getUserEmailSnapshot() ?? '',
    };

    // Focusing the dialog element itself announces the modal to a screen reader without dropping the
    // caret straight into the textarea. Focusing nothing leaves focus on the menu item behind the
    // backdrop, where a keyboard-only user has no signal that anything opened.
    this.dialog.open(ContactSupportDialogComponent, { data, autoFocus: 'dialog' });
  }

  ngOnInit(): void {
    this.store.dispatch(new EngagementAvaliabilityCheck());

    this.engagementsApiService
      .getActionRequiredCount()
      .pipe(
        catchError(() => of(0)),
        take(1),
        takeUntilDestroyed(this.destroyRef),
        // this might be ignored if the module is not loaded, however there is fallback on page load
        tap((count) => this.store.dispatch(new SetEngagementTabCount(count))),
      )
      .subscribe();

    this.showRequestsTab$.pipe(filter(Boolean), take(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      // Feed the every-page nav badge from the cheap `/cxp/tasks/count` endpoint
      // instead of loading the full task list.
      this.store.dispatch(new LoadCxpTaskCount());
    });
  }

  ngAfterViewInit(): void {
    if (this.helpMenuTrigger) {
      this.helpMenuTrigger.menuOpened.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        this.onHelpMenuOpened();
      });
    }
  }

  signIn(): void {
    this.authService.signOut().pipe(take(1)).subscribe();
  }

  register(): void {
    this.authService.navigateToRegistration().pipe(take(1)).subscribe();
  }

  logout(): void {
    this.authService.signOut().pipe(take(1)).subscribe();
  }

  addClientKey(): void {
    this.router.navigateByUrl('clientkey');
  }

  goToAccountSettings(): void {
    this.router.navigateByUrl(`/${ROUTE_TOKEN_MAIN_BASE}/accountSettings`);
  }

  copyMailingAddress(): void {
    const address = this.firmContact.address;
    if (!address) {
      return;
    }

    const copied = this.clipboard.copy(address);
    this.snackbarService.open(
      copied ? 'Mailing Address Copied to Clipboard' : 'Could not copy the mailing address',
      copied ? SnackbarType.SUCCESS : SnackbarType.ERROR,
    );
  }

  onHelpMenuOpened(): void {
    // Add data-testid to the overlay panel when menu opens
    if (this.helpMenu && this.helpMenu.panelId) {
      const panelElement = document.getElementById(this.helpMenu.panelId);
      if (panelElement) {
        panelElement.setAttribute(DATA_SELECTOR_ATTRIBUTE, 'main-header-help-menu');
      }
    }
  }
}
